import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import FilterCard from '../../components/filter-card'
import { UserTypeEnum, ApplicationStatusOptions, ApplicationPeriodOptions, CommonAllOption } from '../../utils/enums'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'

const ApplicationsFilter = ({ filters, onFilterChange, onFilterSubmit, onClearFilters, onRefresh, loading }) => {
  const dispatch = useDispatch()
  const [students, setStudents] = useState([])
  const [programs, setPrograms] = useState([])

  // Fetch students for filter dropdown
  useEffect(() => {
    dispatch(
      ApiCallActions.Get({
        url: 'students',
        query: { limit: 100 },
        onSuccess: ({ data }) => {
          setStudents(data.data || [])
        },
        onError: () => {
          setStudents([])
        }
      })
    )
  }, [dispatch])

  // Fetch programs for filter dropdown
  useEffect(() => {
    dispatch(
      ApiCallActions.Get({
        url: 'programs',
        query: { limit: 100 },
        onSuccess: ({ data }) => {
          setPrograms(data.data || [])
        },
        onError: () => {
          setPrograms([])
        }
      })
    )
  }, [dispatch])

  // Generate year options (current year and previous 10 years)
  const currentYear = new Date().getFullYear()
  const yearOptions = [
    CommonAllOption,
    ...Array.from({ length: 11 }, (_, i) => {
      const year = currentYear - i
      return { value: year.toString(), label: year.toString() }
    })
  ]

  // Period options with "All" option
  const periodOptions = [
    CommonAllOption,
    ...ApplicationPeriodOptions
  ]

  // Student options for select
  const studentOptions = students.map(student => ({
    value: student.id.toString(),
    label: student?.user 
      ? `${student.user.firstName || ''} ${student.user.lastName || ''}`.trim() || student.user.email
      : student.email || student.id
  }))

  // Program options for select
  const programOptions = programs.map(program => ({
    value: program.id.toString(),
    label: program.name || program.id
  }))

  // Status options with "All" option
  const statusOptions = [
    CommonAllOption,
    ...ApplicationStatusOptions
  ]

  const fields = [
    {
      type: 'select',
      name: 'student_id',
      placeholder: 'APPLICATIONS_FILTERS_STUDENT_PLACEHOLDER',
      options: [
        CommonAllOption,
        ...studentOptions
      ],
      icon: 'zmdi zmdi-account',
      roleBased: true,
      roleAccess: [UserTypeEnum.ADMIN,UserTypeEnum.AGENT]
    },
    {
      type: 'select',
      name: 'agent_id',
      placeholder: 'APPLICATIONS_FILTERS_AGENT_PLACEHOLDER',
      roleBased: true,
      roleAccess: UserTypeEnum.ADMIN,
      fetchUrl: 'agents/active',
      prependOptions: [CommonAllOption],
      valueKey: 'id',
      labelKey: 'companyName',
      mapOption: (agent) => {
        const value = agent.id || agent.userId || agent.agentId
        const label =
          agent.companyName ||
          agent.name ||
          agent.fullName ||
          (agent.user && `${agent.user.firstName || ''} ${agent.user.lastName || ''}`.trim())

        if (!value || !label) return null

        return {
          value: value.toString(),
          label: label.toString()
        }
      },
      icon: 'zmdi zmdi-account-box'
    },
    {
      type: 'select',
      name: 'program_id',
      placeholder: 'APPLICATIONS_FILTERS_PROGRAM_PLACEHOLDER',
      options: [
        CommonAllOption,
        ...programOptions
      ]
    },
    {
      type: 'select',
      name: 'status',
      placeholder: 'APPLICATIONS_FILTERS_STATUS_PLACEHOLDER',
      options: statusOptions
    },
    {
      type: 'select',
      name: 'year',
      placeholder: 'APPLICATIONS_FILTERS_YEAR_PLACEHOLDER',
      options: yearOptions
    },
    {
      type: 'select',
      name: 'period',
      placeholder: 'APPLICATIONS_FILTERS_PERIOD_PLACEHOLDER',
      options: periodOptions
    },
    {
      type: 'input',
      name: 'start_date',
      placeholder: 'APPLICATIONS_FILTERS_START_DATE_PLACEHOLDER',
      icon: 'zmdi zmdi-calendar',
      inputType: 'date'
    },
    {
      type: 'input',
      name: 'end_date',
      placeholder: 'APPLICATIONS_FILTERS_END_DATE_PLACEHOLDER',
      icon: 'zmdi zmdi-calendar',
      inputType: 'date'
    }
  ]

  const defaultFilters = {
    student_id: '',
    agent_id: '',
    program_id: '',
    status: '',
    year: '',
    period: '',
    start_date: '',
    end_date: ''
  }

  return (
    <FilterCard
      filters={filters}
      onFilterChange={onFilterChange}
      onFilterSubmit={onFilterSubmit}
      onClearFilters={onClearFilters}
      onRefresh={onRefresh}
      loading={loading}
      title="APPLICATIONS_FILTERS_TITLE"
      titleHide="APPLICATIONS_FILTERS_HIDE"
      titleShow="APPLICATIONS_FILTERS_SHOW"
      fields={fields}
      defaultFilters={defaultFilters}
      autoSubmitFields={['status', 'student_id', 'agent_id', 'program_id', 'year', 'period']}
    />
  )
}

export default ApplicationsFilter

